/**
 * Shared module tiles for sign-up and organization subscription settings.
 * Pricing / sections / copy stay in sync with the signup experience.
 * Set `available: false` to show a tile as not selectable (ongoing integration).
 * Keep sellable-module keys aligned with `backend/shared/constants/platformModules.js` trial list.
 *
 * Monthly PHP tiers (4500–5500): higher where the module has more surface area,
 * cross-module integrations (especially Accounting), and operational complexity.
 */
import { MODULE_BRAND_HEX } from "./moduleBrand";

export const MODULE_PRICING = [
  {
    key: "Accounting",
    label: "Accounting",
    price: 5500,
    color: MODULE_BRAND_HEX.Accounting,
    available: true,
  },
  {
    key: "HR",
    label: "HR",
    price: 5150,
    color: MODULE_BRAND_HEX.HR,
    available: true,
  },
  {
    key: "Inventory",
    label: "Inventory",
    price: 5300,
    color: MODULE_BRAND_HEX.Inventory,
    available: true,
  },
  {
    key: "Marketing",
    label: "Marketing",
    price: 4500,
    color: MODULE_BRAND_HEX.Marketing,
    available: true,
  },
  {
    key: "Procurement",
    label: "Procurement",
    price: 4700,
    color: MODULE_BRAND_HEX.Procurement,
    available: true,
  },
  {
    key: "Sales",
    label: "Sales",
    price: 4850,
    color: MODULE_BRAND_HEX.Sales,
    available: true,
  },
  {
    key: "Production",
    label: "Production",
    tileImageLabel: "Production",
    price: 5000,
    color: MODULE_BRAND_HEX.Production,
    available: true,
  },
  {
    key: "PointOfSale",
    label: "Point of Sale (POS)",
    tileImageLabel: "POS",
    price: 4600,
    color: MODULE_BRAND_HEX.PointOfSale,
    available: false,
  },
];

/** `available === false` tiles are visible but not selectable. */
export function isModuleAvailable(item) {
  return item && item.available !== false; 
}

export const MODULE_DETAILS = {
  Accounting: {
    sections: [
      "Accounting Dashboard",
      "General Ledger",
      "Financial Statements",
      "Invoices",
      "Bills",
      "Expenses",
      "Expense Categories",
      "Journal Entries",
      "Journal Lines",
      "Payments",
    ],
    functionalities: [
      "Chart of accounts and account creation from General Ledger",
      "Posted journal lines, entries, and period-based financial statements",
      "Customer invoices and supplier bills with payment allocation",
      "Expense capture with GL expense accounts and categories",
      "Bill and invoice payments with enriched list views",
      "WebSocket-friendly lists for bills, journal activity, and GL summaries",
    ],
  },
  HR: {
    sections: [
      "HR Dashboard",
      "Employees",
      "Departments",
      "Attendance",
      "Payroll Transactions",
      "Premium Rates",
      "Holidays",
      "Salary Structures",
      "Designations",
      "Employee Schedules",
    ],
    functionalities: [
      "Employee master data with departments and designations",
      "Attendance records, holiday calendars, and per-employee work schedules for payroll",
      "Salary structures, premium rates, and payroll transaction runs",
      "Accounting integration path for payroll posting where configured",
    ],
  },
  Inventory: {
    sections: [
      "Inventory Dashboard",
      "Catalog Items (products)",
      "Stocks",
      "Warehouses",
      "Inventory Transactions",
      "Inventory Adjustments",
      "Purchase Requests (with Procurement)",
      "Goods Receipts (with Procurement)",
      "Sales Orders (with Sales)",
    ],
    functionalities: [
      "Multi-warehouse stock, min/max, and low-stock awareness",
      "Inventory transactions and adjustments with audit trail",
      "Goods receipts and purchase requests when Procurement is also subscribed",
      "Sales order fulfillment from stock when Sales is also subscribed",
      "Alignment with Accounting for inventory-related postings where enabled",
    ],
  },
  Marketing: {
    sections: [
      "Marketing Dashboard",
      "Campaigns",
      "Leads",
      "Marketing Activities",
    ],
    functionalities: [
      "Campaign planning and lead pipeline tracking",
      "Marketing activities linked to campaigns and leads",
      "Lead conversion toward Sales opportunities and quotations",
    ],
  },
  Procurement: {
    sections: [
      "Procurement Dashboard",
      "Purchase Requests",
      "Purchase Orders",
      "Goods Receipts (without Inventory)",
      "Supplier Quotes",
    ],
    functionalities: [
      "Purchase requisitions through to purchase orders",
      "Supplier quote headers and line-level pricing (including VAT treatment)",
      "Receiving via goods receipts when Inventory is not on the plan",
      "Ties to Core suppliers and Accounting bills / journal automation",
    ],
  },
  Sales: {
    sections: [
      "Sales Dashboard",
      "Catalog items (products & services)",
      "Opportunities",
      "Quotations",
      "Sales Orders",
    ],
    functionalities: [
      "Opportunity pipeline through quotations and sales orders",
      "Quotation PDF, approval workflow, and conversion to orders",
      "Sales orders with line items and fulfillment hooks for Inventory",
      "Path to Accounting invoices and journal entries when Accounting is on",
    ],
  },
  Production: {
    sections: [
      "Production Dashboard",
      "Bill of materials",
      "Work orders",
      "Production runs & inventory posting",
    ],
    functionalities: [
      "BOMs per finished good, work orders with optional sales-line demand, material availability checks, timed batches, and stock movements via Inventory.",
    ],
  },
  PointOfSale: {
    sections: [
      "POS registers (planned)",
      "Store sessions & tenders (planned)",
      "Receipts & returns (planned)",
      "Catalog sync from Core/Sales (planned)",
    ],
    functionalities: [
      "Retail POS scope is in ongoing integration; tiles preview future pricing only.",
    ],
  },
};

export function moduleImage(label, color) {
  const safeLabel = String(label || "Module").replace(/</g, "");
  return `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="800" height="420" viewBox="0 0 800 420">
  <defs>
    <linearGradient id="bg" x1="0" x2="1" y1="0" y2="1">
      <stop offset="0%" stop-color="${color}" />
      <stop offset="100%" stop-color="#111827" />
    </linearGradient>
  </defs>
  <rect width="800" height="420" fill="url(#bg)" />
  <circle cx="700" cy="90" r="56" fill="rgba(255,255,255,0.15)" />
  <circle cx="120" cy="320" r="92" fill="rgba(255,255,255,0.12)" />
  <text x="56" y="240" fill="white" font-family="Arial, sans-serif" font-size="56" font-weight="700">
    ${safeLabel}
  </text>
</svg>
`)}`;
}
